import React, { useEffect, useMemo } from 'react';

import { RememberMeAction, RememberMeState, initState, reducer } from './reducer';
import { fetchUserData } from './api';

export const RememberMeContext = React.createContext<{
  state: RememberMeState;
  dispatch: React.Dispatch<RememberMeAction>;
}>(null);

export const RememberMeProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initState);
  const startValue = useMemo(() => ({ state: initState, dispatch: () => null }), [initState]);
  const actualValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  useEffect(() => {
    const cookies = document.cookie;
    const regex = /lookupKey=([^;]+)/;
    const match = cookies.match(regex);

    const getData = async () => {
      const data = await fetchUserData(match[1]);
      dispatch({ type: 'updateUserData', payload: data });
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    match && match[1] && getData();
  }, []);

  if (
    (typeof window !== 'undefined' && !window?.Osano?.cm?.personalization) ||
    !process.env.GATSBY_SECRET
  ) {
    return <RememberMeContext.Provider value={startValue}>{children}</RememberMeContext.Provider>;
  }

  return <RememberMeContext.Provider value={actualValue}>{children}</RememberMeContext.Provider>;
};

export function useRememberMe() {
  const context = React.useContext(RememberMeContext);

  if (context === undefined) {
    throw new Error('useRememberMe must be used within a RememberMeProvider');
  }

  return context;
}

/**
 Logic:
 - If saveUserData is true, run post.
    - This creates a new lookupKey cookie every time, which means that as long as the user is coming
      donate/action pages more than once a year, the cookie won't expire (because a new one with a 
      new expiration date will be created on every visit.)
    - This also eliminates the need to do patch calls to update user data.
 - If saveUserData is false but userData exists, run delete.
 - If saveUserData is false but no userData exists, do nothing.
 */
